import React, { useCallback } from 'react';
import { Loading } from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import {
  useCart,
  CartUpdateAction,
  useUpdateCartMutation,
  CartUpdatePropertyName
} from '../../../hooks/useCart';
import { useCheckoutStepTracking, CheckoutStep, useCheckout } from '../../../hooks/useCheckout';
import { useCheckoutFormStates } from '../../CheckoutFormStates';
import { CardPaymentMethodSelector, Mode } from '../CardPaymentMethodSelector';

interface Props {
  onSaved: () => void;
}

export const CheckoutBraintreePaymentMethod: React.FC<Props> = ({ onSaved }) => {
  const {
    cart: {
      attributes: { paymentMethodId: defaultPaymentMethodId }
    },
    loaded: cartLoaded,
    error: cartError
  } = useCart();
  const { isPaymentMethodSelected } = useCheckout();
  const [updateCart] = useUpdateCartMutation();
  const { trackCheckoutStepView, trackCheckoutStepComplete } = useCheckoutStepTracking(
    CheckoutStep.PaymentMethod
  );
  const { showErrorModal } = useErrorModal();
  const { setFormState } = useCheckoutFormStates();

  const onSubmit = useCallback(
    async (selectedPaymentMethodId: string | null) => {
      try {
        await updateCart(CartUpdateAction.PaymentMethod, {
          [CartUpdatePropertyName.PaymentMethod]: selectedPaymentMethodId
        });
        if (!isPaymentMethodSelected) trackCheckoutStepComplete();
        onSaved();
      } catch (error) {
        reportError('CHECKOUT_BRAINTREE_PAYMENT_SUBMIT', error);
        showErrorModal();
      }
    },
    [onSaved, updateCart, trackCheckoutStepComplete, showErrorModal, isPaymentMethodSelected]
  );

  const onModeChange = useCallback(
    (mode: Mode) => {
      setFormState('paymentMethod', mode === Mode.Resting ? 'closed' : 'open');
      if (!isPaymentMethodSelected && mode === Mode.Creating) trackCheckoutStepView();
    },
    [trackCheckoutStepView, isPaymentMethodSelected, setFormState]
  );

  if (!cartLoaded) return <Loading />;
  if (cartError) throwQueryError(cartError);

  return (
    <CardPaymentMethodSelector
      isChangeable
      onSubmit={onSubmit}
      onModeChange={onModeChange}
      defaultPaymentMethodId={defaultPaymentMethodId}
    />
  );
};
